import { useEffect, useState } from "react";
import { BookOpen, Mail, Sparkles, Zap } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * ActivityFeed — vertical timeline of recent operational events pulled
 * from `/api/admin/activity`. Shows time, glyph, and event description.
 */

const KIND_META = {
  article_published: { icon: BookOpen, tint: "text-emerald-400" },
  article_updated:   { icon: BookOpen, tint: "text-gold" },
  autoseo_import:    { icon: Sparkles, tint: "text-gold" },
  contact_message:   { icon: Mail,     tint: "text-bone/70" },
  system:            { icon: Zap,      tint: "text-bone/70" },
};

function formatWhen(iso, t) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const isYest = d.toDateString() === yest.toDateString();
  const hhmm = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  if (sameDay) return hhmm;
  if (isYest) return `${t("admin.label.yesterday")} · ${hhmm}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + ` · ${hhmm}`;
}

export default function ActivityFeed({ limit = 12, testId = "admin-activity-feed" }) {
  const { t } = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminApi.activity(limit);
        if (!cancelled) setEvents(d.events || []);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  return (
    <div data-testid={testId}>
      {loading && <p className="text-sm text-bone/50">{t("admin.label.loading")}</p>}
      {err && <p className="text-sm text-red-400">Activity error: {err}</p>}
      {!loading && !err && events.length === 0 && (
        <p className="text-sm text-bone/50">{t("admin.notif.none")}</p>
      )}
      <ol className="relative">
        {events.map((e, i) => {
          const meta = KIND_META[e.kind] || KIND_META.system;
          const Icon = meta.icon;
          return (
            <li
              key={`${e.when}-${i}`}
              className="relative pl-10 py-4 border-b border-white/5 last:border-b-0"
              data-testid="admin-activity-item"
            >
              <span className="absolute left-0 top-4 w-7 h-7 rounded-full border border-gold/30 bg-black/40 flex items-center justify-center">
                <Icon size={13} strokeWidth={1.5} className={meta.tint} />
              </span>
              <p className="text-[10px] tracking-[0.24em] uppercase text-bone/50">
                {formatWhen(e.when, t)}
              </p>
              <p className="mt-1 text-[13px] text-bone/85 leading-snug">
                {e.description}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
